import React from "react";
import { type LucideIcon } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export type TimelineTone = "emerald" | "red" | "orange" | "violet" | "blue" | "slate";

const toneStyles: Record<TimelineTone, { bubble: string; icon: string; line: string }> = {
  emerald: { bubble: "bg-emerald-100", icon: "text-emerald-600", line: "bg-emerald-200" },
  red: { bubble: "bg-red-100", icon: "text-red-600", line: "bg-red-200" },
  orange: { bubble: "bg-orange-100", icon: "text-orange-600", line: "bg-orange-200" },
  violet: { bubble: "bg-violet-100", icon: "text-violet-600", line: "bg-violet-200" },
  blue: { bubble: "bg-sky-100", icon: "text-sky-700", line: "bg-sky-200" },
  slate: { bubble: "bg-slate-200", icon: "text-slate-500", line: "bg-slate-200" },
};

/** A single, already-mapped event ready to render on the vertical timeline. */
export interface TimelineDisplayEvent {
  id: string;
  date: string;
  label: string;
  tone: TimelineTone;
  Icon: LucideIcon;
  sublabel?: string;
  actorName?: string | null;
  reason?: string | null;
  struck?: boolean;
}

const fmtDate = (value: string): string => new Date(value).toLocaleDateString("de-DE");

const fmtTime = (value: string): string =>
  new Date(value).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

/**
 * Shared, presentational vertical timeline (date+time column → colored rail bubble
 * → content with actor + optional reason). Used by both the transaction-lifecycle
 * and the export-batch dialogs so they stay visually identical.
 */
export const SepaTimelineView: React.FC<{ events: TimelineDisplayEvent[] }> = ({ events }) => (
  <ScrollArea className="max-h-[540px] pr-3">
    <TooltipProvider delayDuration={200}>
      <ol className="relative">
        {events.map((event, idx) => {
          const tone = toneStyles[event.tone];
          const isLast = idx === events.length - 1;
          return (
            <li key={event.id} className="flex">
              {/* Date column */}
              <div className="w-[92px] flex-shrink-0 pr-3 pt-1 text-right">
                <span className="block whitespace-nowrap text-[0.95rem] font-bold text-slate-600">
                  {fmtDate(event.date)}
                </span>
                <span className="block whitespace-nowrap text-xs font-medium text-slate-400">
                  {fmtTime(event.date)}
                </span>
              </div>

              {/* Rail */}
              <div className="flex flex-col items-center">
                <div
                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${tone.bubble}`}
                >
                  <event.Icon className={`h-[1.15rem] w-[1.15rem] ${tone.icon}`} />
                </div>
                {!isLast && <div className={`w-0.5 flex-1 rounded ${tone.line}`} />}
              </div>

              {/* Content */}
              <div className={`flex-1 pl-3 ${isLast ? "pb-0" : "pb-4"}`}>
                <p
                  className={`text-base font-semibold leading-tight ${
                    event.struck ? "text-red-600 line-through" : "text-slate-900"
                  }`}
                >
                  {event.label}
                </p>

                {(event.sublabel || event.actorName) && (
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 leading-tight">
                    {event.sublabel && (
                      <span className="text-[0.9rem] font-medium text-slate-600">
                        {event.sublabel}
                      </span>
                    )}
                    {event.sublabel && event.actorName && <span className="text-slate-300">·</span>}
                    {event.actorName && (
                      <span className="flex items-center gap-1.5 text-[0.9rem] font-medium text-slate-500">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="bg-slate-300 text-[0.65rem] font-bold text-slate-700">
                                {initials(event.actorName)}
                              </AvatarFallback>
                            </Avatar>
                          </TooltipTrigger>
                          <TooltipContent>{event.actorName}</TooltipContent>
                        </Tooltip>
                        {event.actorName}
                      </span>
                    )}
                  </div>
                )}

                {event.reason && (
                  <p className="mt-1.5 rounded-md border border-solid border-red-100 bg-red-50 px-2.5 py-1.5 text-[0.9rem] font-medium text-red-700">
                    Grund: {event.reason}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </TooltipProvider>
  </ScrollArea>
);
